import { Injectable } from '@angular/core';

import { IntelligenceService } from './intelligence.service';
import { SessionService }      from '../sessions/session.service';

@Injectable({
  providedIn: 'root'
})
export class IntelligenceFormatService {
  constructor (
    private intelligenceService: IntelligenceService,
    private sessionService     : SessionService
  ) {}

  compactMoney ( value : number ) : string {
    const absolute = Math.abs(value || 0);

    if ( absolute >= 1000000 ) {
      return `${ this.symbol() }${ ( value / 1000000 ).toFixed(1) }M`;
    }

    if ( absolute >= 1000 ) {
      return `${ this.symbol() }${ ( value / 1000 ).toFixed(1) }k`;
    }

    return `${ this.symbol() }${ Math.round(value || 0) }`;
  }

  count ( value : number ) : string {
    return ( value || 0 ).toLocaleString();
  }

  downloadCsv ( name : string, rows : Array<any> ) : void {
    if ( !rows || !rows.length ) {
      return;
    }

    const headers = Object.keys(rows[ 0 ]);
    const escape = ( value : any ) : string => {
      let text = value === null || value === undefined
        ? ''
        : String(value);

      if ( /^[=+\-@\t\r]/.test(text) ) {
        text = `'${ text }`;
      }

      return `"${ text.replace(/"/g, '""') }"`;
    };
    const lines = [ headers, ...rows.map(row => headers.map(header => row[ header ])) ]
      .map(line => line.map(escape).join(','));

    const range = this.intelligenceService.range;
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    const blob = new Blob([ lines.join('\r\n') ], { type: 'text/csv;charset=utf-8;' });
    const anchor = document.createElement('a');

    anchor.href = URL.createObjectURL(blob);
    anchor.download = `intelligence_${ slug }_${ range.start_date }_${ range.end_date }.csv`;
    anchor.click();

    URL.revokeObjectURL(anchor.href);
  }

  date ( value : string ) : string {
    const parsed = new Date(`${ value }T00:00:00`);

    return isNaN(parsed.getTime())
      ? value
      : parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  }

  hours ( value : number ) : string {
    return `${ ( value || 0 ).toFixed(1) }h`;
  }

  money ( value : number ) : string {
    const amount = ( value || 0 ).toLocaleString(undefined, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    });

    return `${ this.symbol() }${ amount }`;
  }

  percent ( value : number ) : string {
    return value === null || value === undefined
      ? '—'
      : `${ value.toFixed(1) }%`;
  }

  sortRows<RowType> ( rows : Array<RowType>, field : string, direction : string ) : Array<RowType> {
    const factor = direction === 'asc'
      ? 1
      : -1;
    const normalise = ( value : any ) : any => typeof value === 'string' && value.trim() !== '' && !isNaN(Number(value))
      ? Number(value)
      : value;

    return [ ...rows ].sort(( first, second ) => {
      const left = normalise(first[ field ]);
      const right = normalise(second[ field ]);

      if ( left === right ) {
        return 0;
      }

      if ( left === null || left === undefined ) {
        return 1;
      }

      if ( right === null || right === undefined ) {
        return -1;
      }

      return left > right
        ? factor
        : -factor;
    });
  }

  symbol () : string {
    const session = this.sessionService.base();

    return ( session.country_settings && session.country_settings.currency ) || '$';
  }
}
